import {
  DefectCatalogItem,
  toDefectMeasurementSlug,
  toDefectSlug,
  toDefectSurveyYearSlug,
} from '../contracts/availableLayers';
import {
  CRACKMAP_INITIAL_ZOOM,
  CRACKMAP_WMTS_FORMAT,
  CRACKMAP_WMTS_SERVICE_URL,
  CRACKMAP_WMTS_TILE_MATRIX_SET,
} from '../crackmaps/crackmapCatalog';
import type { DefectOverviewGridConfig } from '../defectOverlays/overviewGrid';
import {
  LINE_CONDITION_RAG_COLOR_SCHEME,
  LINE_CONDITION_RAG_THRESHOLDS,
} from './lineConditionBands';

export const WINDSOR_LINE_CONDITION_TYPE_NAME =
  'wfs_layers:windsor-mfv-lanemarkings-2026-27';

export const WINDSOR_LINE_CONDITION_WFS_URL =
  `https://geoserver.xais.co.uk/geoserver/wfs_layers/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=${encodeURIComponent(WINDSOR_LINE_CONDITION_TYPE_NAME)}`;

export const LINE_CONDITION_WFS_PROPERTY_NAMES =
  'geometry,sectionID,Type,maxWidth,condition,reflect';

export const LINE_CONDITION_OVERVIEW_WMTS_STYLE = 'line_condition';

export const WINDSOR_LINE_CONDITION_OVERVIEW_WMTS: DefectOverviewGridConfig = {
  serviceUrl: CRACKMAP_WMTS_SERVICE_URL,
  tileMatrixSet: CRACKMAP_WMTS_TILE_MATRIX_SET,
  format: CRACKMAP_WMTS_FORMAT,
  bands: [
    {
      layerId: 'wfs_layers:windsor-mfv-lanemarkings-100mgrid-2026-27',
      style: LINE_CONDITION_OVERVIEW_WMTS_STYLE,
      minZoom: 0,
      maxZoom: 13,
    },
    {
      layerId: 'wfs_layers:windsor-mfv-lanemarkings-50mgrid-2026-27',
      style: LINE_CONDITION_OVERVIEW_WMTS_STYLE,
      minZoom: 13,
      maxZoom: 15,
    },
    {
      layerId: 'wfs_layers:windsor-mfv-lanemarkings-10mgrid-2026-27',
      style: LINE_CONDITION_OVERVIEW_WMTS_STYLE,
      minZoom: 15,
      maxZoom: 17,
    },
    {
      layerId: 'wfs_layers:windsor-mfv-lanemarkings-5mgrid-2026-27',
      style: LINE_CONDITION_OVERVIEW_WMTS_STYLE,
      minZoom: 17,
      maxZoom: CRACKMAP_INITIAL_ZOOM,
    },
  ],
};

export function resolveLineConditionOverviewWmts(
  sourceLayerId: string | null | undefined,
): DefectOverviewGridConfig | null {
  const trimmed = sourceLayerId?.trim();
  if (!trimmed) {
    return null;
  }
  if (trimmed === WINDSOR_LINE_CONDITION_TYPE_NAME) {
    return WINDSOR_LINE_CONDITION_OVERVIEW_WMTS;
  }
  return null;
}

export function isLineConditionCatalogEnabled(): boolean {
  return process.env.REACT_APP_LINE_CONDITION_WFS_ENABLED !== 'false';
}

export function getLineConditionCatalogDefects(): readonly DefectCatalogItem[] {
  if (!isLineConditionCatalogEnabled()) {
    return [];
  }

  return [
    {
      id: 'line-condition-windsor-mfv',
      slug: toDefectSlug('windsor-mfv-line-condition'),
      surveyType: 'mfv',
      name: 'Line Condition',
      code: 'LINECOND',
      type: 'Road Surface',
      presentation: 'line-condition',
      measurements: [
        {
          name: 'Line Condition',
          slug: toDefectMeasurementSlug('line-condition-overlay'),
          code: 'LINEO',
          description:
            'Line-condition polylines from GeoServer: grid overview when zoomed out; full WFS at street zoom (condition 0–1, higher = better: red 0–0.39, amber 0.4–0.69, green 0.7–1)',
          parameter: 'line-condition',
          colorScheme: LINE_CONDITION_RAG_COLOR_SCHEME,
          thresholds: [...LINE_CONDITION_RAG_THRESHOLDS],
          surveyYears: [
            {
              id: 'windsor-2026-27',
              slug: toDefectSurveyYearSlug('windsor-2026-27'),
              surveyYear: '2026-27',
              startDate: '2026-04-01',
              endDate: '2027-03-31',
              sourceLayerId: WINDSOR_LINE_CONDITION_TYPE_NAME,
              vectorSourceUrl: WINDSOR_LINE_CONDITION_WFS_URL,
              lanes: ['all'],
            },
          ],
        },
      ],
    },
  ];
}
